import React, { useEffect, useRef, useState } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import { MdOutlineNoPhotography } from 'react-icons/md';
import {
  Divider,
  IconButton,
  Pagination,
  Modal,
  Box,
  Button,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { IoCloseOutline } from 'react-icons/io5';
import objectStore from '../../stores/objectStore';
import { observer } from 'mobx-react-lite';
import RenderSelectedTags from '../settings/tags/RenderSelectedTags';

import ModalObjectCard from './ModalObjectCard';
import requestStore from '../../stores/requestStore';
import logger from '../../utils/logger';
import { getPrice } from '../../utils/priceUtils';
import { getObjectFullInfo } from '../../utils/objectInfoUtils';
import { scopedObjectSource } from '../../tenancy/scopedObjectDto';


function ListObjectRequest({ objects }) {
  const isMounted = useRef(true);
  const [object, setObject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Состояние для модального окна
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:360px)');

  // Убираем локальную пагинацию, так как теперь она происходит на уровне запроса к серверу
  // и объекты уже приходят порциями

  useEffect(() => {
    return () => {
      isMounted.current = false;
      objectStore.objectsForRequest = [];
      /* objectStore.excludedObjectsFromRequest = [] */
    };
  }, []);

  // Функция-обработчик удаления
  const handleDelete = (objectId) => {
    logger.log('Delete object with id:', objectId);
    objectStore.deleteObjectFromNewRequest(objectId);
  };

  const handleOpenObject = async (objectId) => {
    const obj = objects.find((obj) => obj.id === objectId);
    setObject(obj);
    setIsModalOpen(true); // Открываем модальное окно
  };

  const handleCloseModal = () => {
    setIsModalOpen(false); // Закрываем модальное окно
    setObject(null); // Очищаем объект (опционально)
  };

  // Добавить объект в список "Не подходит"
  function addToNotSuited(objectId, e) {
    e.stopPropagation();
    requestStore.addToNotSuitedRequest(objectId);
  }
  // Добавить объект в список "Подходит"
  function addToSuited(objectId, e) {
    e.stopPropagation();
    requestStore.addToSuitedRequest(objectId);
  }

  // Функция для получения краткого описания (первые 100 символов)
  const getShortDescription = (description) => {
    if (!description) return '';
    return description.length > 100 ? `${description.substring(0, 100)}...` : description;
  };

  return (
    <>
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && (
        <div onClick={() => logger.log('ListObjectRequest Render')}>% ListObjectRequest %</div>
      )}

      <List sx={{ width: '100%', maxWidth: '100%', bgcolor: 'background.paper' }}>
        {objects.map((obj, index) => {
          const photoUrl = obj.photos?.[0]?.imageUrl || null;

          return (
            <React.Fragment key={obj.id}>
              <Paper
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mt: 1,
                  p: isMobile ? 1 : 2,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
                onClick={() => handleOpenObject(obj.id)}>
      
                 
                {isMobile ? (
                  // Мобильная версия - фото вверху, описание внизу
                  <div style={{ width: '100%' }}>
                    {photoUrl ? (
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Avatar
                          variant="rounded"
                          src={photoUrl}
                          sx={{
                            width: '100%',
                            height: isSmallMobile ? '150px' : '180px',
                            borderRadius: '8px',
                          }}
                        />
                      </Box>
                    ) : null}
                    <div>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mb: 0.5,
                          fontSize: '0.7rem',
                        }}>
                        {getObjectFullInfo(obj, objectStore.categories)}
                      </Typography>
                      <div className={isSmallMobile ? 'text-base' : 'text-lg'}>{obj.address}</div>
                      {obj.description && !isSmallMobile && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                          {getShortDescription(obj.description)}
                        </Typography>
                      )}
                      <div className="font-bold mt-1">
                        {getPrice(obj.details).value} {getPrice(obj.details).suffix}
                      </div>
                      <div className="mt-2">
                        <RenderSelectedTags tags={obj.details?.tags} />
                      </div>

                      {scopedObjectSource(obj.source) && (
                        <Box className="mt-2 p-1 border-t border-gray-200">
                          <Box className="flex items-center gap-1">
                            <Typography className="font-medium">Источник:</Typography>
                            <span className="text-blue-500 font-bold">{scopedObjectSource(obj.source).name}</span>
                          </Box>
                        </Box>
                      )}

                      <div className="flex flex-row mt-4">
                        <Button
                          variant={
                            requestStore.suitedObjectForRequest.includes(obj.id)
                              ? 'contained'
                              : 'outlined'
                          }
                          size="small"
                          color="primary"
                          onClick={(e) => addToSuited(obj.id, e)}>
                          Добавить в заявку
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Десктопная версия - как было
                  <div>
                    <div className="flex flex-row">
                      <ListItemAvatar>
                        {photoUrl ? (
                          <Avatar
                            variant="rounded"
                            src={photoUrl}
                            sx={{ width: '60px', height: '60px' }}
                          />
                        ) : (
                          <Avatar sx={{ width: '60px', height: '60px' }}>
                            <MdOutlineNoPhotography className="text-3xl" />
                          </Avatar>
                        )}
                      </ListItemAvatar>

                      <div
                        style={{
                          display: 'flex',
                          flex: 1,
                          justifyContent: 'space-between',
                          paddingLeft: '10px',
                        }}>
                        <div>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              mb: 1,
                              fontSize: '0.7rem',
                            }}>
                            {getObjectFullInfo(obj, objectStore.categories)}
                          </Typography>
                          <div className="text-lg font-bold">{obj.renderTitle}</div>
                          <div className="text-sm">{obj.address}</div>
                          {obj.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1, mb: 1 }}>
                              {getShortDescription(obj.description)}
                            </Typography>
                          )}
                          <div>
                            {getPrice(obj.details).value} {getPrice(obj.details).suffix}
                          </div>
                          <div className="mt-2">
                            <RenderSelectedTags tags={obj.details?.tags} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {scopedObjectSource(obj.source) && (
                      <Box className="mt-2 p-1 border-t border-gray-200">
                        <Box className="flex items-center gap-1">
                          <Typography className="font-medium">Источник:</Typography>
                          <span className="text-blue-500 font-bold">{scopedObjectSource(obj.source).name}</span>
                        </Box>
                      </Box>
                    )}

                    <div className="flex flex-row mt-4">
                      <Button
                        variant={
                          requestStore.suitedObjectForRequest.includes(obj.id)
                            ? 'contained'
                            : 'outlined'
                        }
                        size="small"
                        color="primary"
                        onClick={(e) => addToSuited(obj.id, e)}>
                        Добавить в заявку
                      </Button>
                    </div>
                  </div>
                )}
              </Paper>

              {index < objects.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>

      {/* Модальное окно с ModalObjectCard */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translate(-50%, 0)',
            width: '97%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            maxHeight: '95vh',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            [theme.breakpoints.up('sm')]: { width: '80%' },
            [theme.breakpoints.up('md')]: { width: '70%' },
            [theme.breakpoints.up('lg')]: { width: '60%' },
            [theme.breakpoints.up('xl')]: { width: '50%' },
          })}>
          <Box sx={{ p: 2 }}>
            <ModalObjectCard
              onClose={handleCloseModal}
              object={object}
              source="request"
              // title и onDelete можно добавить, если они нужны
              // title="Some Title"
              // onDelete={someDeleteFunction}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default observer(ListObjectRequest);
